import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProjectForecast } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { ForecastGrid, groupContributions, type ForecastGridRow } from './ForecastGrid.js';
import { useProjectForecast } from './api.js';

/**
 * Filters a project forecast by the two series toggles and returns the rolled-up month
 * totals + grand total of the visible rows. Shared by the panel and the page-level
 * combined-totals grids so both react to the same toggles.
 */
export function filterProjectTotals(
  forecast: ProjectForecast | undefined,
  showRemaining: boolean,
  showUnbilled: boolean,
): { totalsByMonth: Record<string, string>; total: string } {
  const visible = (forecast?.groups ?? []).filter(
    (g) => (g.kind === 'remaining' && showRemaining) || (g.kind === 'unbilled' && showUnbilled),
  );
  const monthTotals: Record<string, number> = {};
  let total = 0;
  for (const g of visible) {
    for (const [month, amount] of Object.entries(g.totalsByMonth)) {
      monthTotals[month] = (monthTotals[month] ?? 0) + Number(amount);
      total += Number(amount);
    }
  }
  const monthTotalsStr: Record<string, string> = {};
  for (const [month, amount] of Object.entries(monthTotals))
    monthTotalsStr[month] = amount.toFixed(2);
  return { totalsByMonth: monthTotalsStr, total: total.toFixed(2) };
}

interface ProjectForecastPanelProps {
  /** When false, the panel does not fetch (e.g. user lacks the projects permission). */
  enabled?: boolean;
  defaultCollapsed?: boolean;
  /** Pre-fetched forecast (e.g. the public portal). When provided, the panel does not fetch. */
  data?: ProjectForecast;
  /** When false, drill-down rows do not navigate (read-only public view). */
  interactive?: boolean;
  /** Controlled "Remaining budget revenue" toggle (lifts state so combined totals react). */
  showRemaining?: boolean;
  onShowRemainingChange?: (value: boolean) => void;
  /** Controlled "Un-billed hours" toggle. */
  showUnbilled?: boolean;
  onShowUnbilledChange?: (value: boolean) => void;
}

/**
 * Revenue forecast for active projects, grouped by the project header's default service
 * category, in two independently-toggled series: remaining budget revenue (unused budget
 * hours × rate, spread across the remaining months) and un-billed hours (revenue of
 * logged-but-not-yet-invoiced hours, landing in each hour's own work-date month). The
 * month totals recompute from the visible rows. Drill-down opens the project. The toggles
 * can be controlled by a parent so page-level combined totals stay in sync.
 */
export function ProjectForecastPanel({
  enabled = true,
  defaultCollapsed,
  data,
  interactive = true,
  showRemaining: showRemainingProp,
  onShowRemainingChange,
  showUnbilled: showUnbilledProp,
  onShowUnbilledChange,
}: ProjectForecastPanelProps): JSX.Element {
  const navigate = useNavigate();
  const query = useProjectForecast(enabled && data === undefined);
  const [internalRemaining, setInternalRemaining] = useState(true);
  const [internalUnbilled, setInternalUnbilled] = useState(false);
  const showRemaining = showRemainingProp ?? internalRemaining;
  const showUnbilled = showUnbilledProp ?? internalUnbilled;
  const setShowRemaining = onShowRemainingChange ?? setInternalRemaining;
  const setShowUnbilled = onShowUnbilledChange ?? setInternalUnbilled;

  const forecast = data ?? query.data;
  const months = forecast?.months ?? [];

  const { rows, totalsByMonth, grandTotal } = useMemo(() => {
    const visible = (forecast?.groups ?? []).filter(
      (g) => (g.kind === 'remaining' && showRemaining) || (g.kind === 'unbilled' && showUnbilled),
    );
    const gridRows: ForecastGridRow[] = visible.map((g) => ({
      key: g.key,
      label: (
        <span className="inline-flex items-center gap-2">
          <Badge tone={g.kind === 'remaining' ? 'green' : 'blue'}>
            {g.kind === 'remaining' ? 'Remaining budget' : 'Un-billed hours'}
          </Badge>
          <span>{g.category}</span>
        </span>
      ),
      total: g.total,
      totalsByMonth: g.totalsByMonth,
      contributions: groupContributions(g.contributions),
    }));

    const { totalsByMonth: monthTotalsStr, total } = filterProjectTotals(
      forecast,
      showRemaining,
      showUnbilled,
    );
    return { rows: gridRows, totalsByMonth: monthTotalsStr, grandTotal: total };
  }, [forecast, showRemaining, showUnbilled]);

  const toggle = (
    <span className="flex items-center gap-3 text-xs">
      <label className="inline-flex cursor-pointer items-center gap-1.5">
        <input
          type="checkbox"
          checked={showRemaining}
          onChange={(e) => setShowRemaining(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-slate-300"
        />
        <span className="font-medium text-emerald-700">Remaining budget revenue</span>
      </label>
      <label className="inline-flex cursor-pointer items-center gap-1.5">
        <input
          type="checkbox"
          checked={showUnbilled}
          onChange={(e) => setShowUnbilled(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-slate-300"
        />
        <span className="font-medium text-sky-700">Un-billed hours</span>
      </label>
    </span>
  );

  return (
    <ForecastGrid
      title="Project revenue forecast"
      subtitle="by service category"
      months={months}
      rows={rows}
      grandTotal={grandTotal}
      totalsByMonth={totalsByMonth}
      onOpenSource={interactive ? (id) => navigate(`/projects/${id}`) : undefined}
      isLoading={data === undefined && query.isLoading}
      isError={data === undefined && query.isError}
      emptyText="No active projects with remaining budget or un-billed hours to forecast yet."
      headerExtra={toggle}
      defaultCollapsed={defaultCollapsed}
    />
  );
}
